/**
 * The owner whose `%Name` table a rendered node resolves against, that table at composed live
 * paths, and the local scene a local-to-scene resource on the node measures from. Each instance
 * root has a table of its own, which an outer consumer never sees (`uniqueNameOwner.md`). The
 * collapse composes an instance's table, and this reads it. Pure: it walks the composed live tree
 * over a cache snapshot, like `liveSceneTree.ts`, and the React hooks supply reactivity.
 */
import { NO_OWNER } from '../parser/nodeOwner.js';
import { belowRoot, joinPath, parentPathOf, pathBetween, pathUnder } from '../utils/nodePath.js';
import { UNIQUE_NODE_PREFIX, nodePathWalkNames, simplifiedNodePath } from '../godot/string/nodePath.js';
import { cachedUniqueNameLivePaths, cachedUniqueNameOwnership, cachedUniqueNamesBelowRoot, } from '../utils/uniqueNames.js';
import { perObject } from '../utils/perObject.js';
import { passesContentTrail, simplifiedLivePath } from '../parser/idTrail.js';
import { placedBelow } from '../resources/instanceNameTable.js';
import { holdsInstanceContent, liveTreeOf, } from './liveSceneTree.js';
/** A composed live path spelled the way `owner`'s own file spells it. */
function fileLocalPath(owner, livePath) {
    if (!owner.parent)
        return livePath;
    return pathUnder(owner.roots[0].name, pathBetween(owner.path, livePath));
}
/** The inverse: a live path from `owner`'s file, composed under its render path. */
function composedPath(owner, fileLivePath) {
    if (!owner.parent)
        return fileLivePath;
    return pathUnder(owner.path, belowRoot(fileLivePath));
}
const ROOT_OWNED = (headings) => headings.isRootOwned;
/** The file that adds a node holds its heading's `owner=`: an override's never reaches the folded node. */
const ADDED = (headings) => headings.isAdded;
/** Whether `owner`'s file seats a heading that `counts` at `livePath`, placed over the live tree. */
function seatsInsideInstance(owner, livePath, counts) {
    const headings = cachedUniqueNameOwnership(owner.roots).insideInstances.get(fileLocalPath(owner, livePath));
    if (headings !== undefined && counts(headings))
        return true;
    return uniqueSpeltHeadings(owner.roots).some(([entry, spelt]) => counts(spelt) && owner.live.placed(composedPath(owner, entry)) === livePath);
}
/** A file's headings inside instances whose live paths pass through a `%Name`. */
const uniqueSpeltHeadings = perObject((roots) => [...cachedUniqueNameOwnership(roots).insideInstances].filter(([path]) => path.includes(UNIQUE_NODE_PREFIX)));
/** The nearest file above `owner` that seats a heading `counts` at `livePath`, else `owner`. */
function fileSeating(owner, livePath, counts) {
    for (let above = owner; above.parent; above = above.parent) {
        if (seatsInsideInstance(above.parent, livePath, counts))
            return above.parent;
    }
    return owner;
}
/** Whether a live path passes through a `%Name` or a content trail, where only a graft knows the node. */
function isPlacedByGraft(livePath) {
    return livePath.includes(UNIQUE_NODE_PREFIX) || passesContentTrail(livePath);
}
/** The live tree with each live path a graft places ({@link isPlacedByGraft}) placed once per walk. */
class PlacedLiveTree {
    tree;
    /** Written only by `placed`. */
    placements = new Map();
    constructor(tree) {
        this.tree = tree;
    }
    /**
     * `livePath` with each host sub-path through a `%Name` or a content trail replaced by where the
     * graft placed it, as of its heading (`uniqueParentPlacements`). A path the loaded tree cannot follow keeps the rest
     * as written, and so does a heading its file seats through a `%Name` the graft could not reach.
     */
    placed(livePath) {
        if (!isPlacedByGraft(livePath))
            return livePath;
        const cached = this.placements.get(livePath);
        if (cached !== undefined)
            return cached;
        let segments = livePath.split('/');
        for (let depth = 0; depth < segments.length; depth++) {
            const link = this.tree.at(segments.slice(0, depth + 1).join('/'));
            if (!link)
                break;
            const placements = link.step.node.uniqueParentPlacements;
            if (placements) {
                segments = [...segments.slice(0, depth + 1), ...placedBelow(placements, segments.slice(depth + 1))];
            }
        }
        // A child its file seated outside the instance climbs out of it with `..`.
        const spelt = segments.join('/');
        const placed = segments.includes('..') ? simplifiedLivePath(spelt) : spelt;
        this.placements.set(livePath, placed);
        return placed;
    }
}
/**
 * The owner walk over one live tree, each live path stepped to once however many walks pass
 * through it: the node's own walk, each `%Name` an `owner=` reads, and the walk to that owner.
 */
class OwnerWalk {
    live;
    /** Written only by `to`. A null marks a path the loaded tree does not contain. */
    reached = new Map();
    root;
    constructor(live) {
        this.live = live;
        const { roots, rootGroups } = live.tree;
        const outer = { path: roots[0]?.name ?? '', roots, live };
        const candidates = rootGroups.map((group) => ({ group, owner: outer }));
        this.root = { owner: outer, into: outer, candidates };
    }
    to(path) {
        let reached = this.root;
        let parentPath = '';
        for (const segment of path.split('/')) {
            const livePath = joinPath(parentPath, segment);
            if (!this.reached.has(livePath))
                this.reached.set(livePath, this.step(reached, livePath));
            const next = this.reached.get(livePath);
            if (!next)
                return { owner: reached.owner, into: reached.owner };
            reached = next;
            parentPath = livePath;
        }
        return reached;
    }
    /** The walk one segment on from `from` to `livePath`, or null where none is there. */
    step(from, livePath) {
        const { live } = this;
        const link = live.tree.at(livePath);
        const found = link && from.candidates.find((candidate) => candidate.group === link.group);
        if (!link || !found)
            return null;
        const node = link.raw;
        // A node the file above grafted in is that file's own, however deep it sits.
        const owner = fileSeating(found.owner, livePath, ROOT_OWNED);
        const subRoots = link.step.instancePath ? live.tree.sceneNodes(link.step.instancePath) : undefined;
        // The instance node is the root of what it instances, so it owns the GLB's objects too: they sit
        // directly below it (packed_scene.cpp:261-264).
        const instanceOwner = subRoots && { path: livePath, roots: subRoots, parent: owner, live };
        const candidates = link.step.groups.map((group) => ({
            group,
            owner: instanceOwner && holdsInstanceContent(group)
                ? { ...instanceOwner, composed: link.step.node.uniqueNames }
                : owner,
        }));
        const into = instanceOwner
            ? (candidates.find((c) => holdsInstanceContent(c.group))?.owner ?? owner)
            : owner;
        return { owner, into, node, candidates };
    }
}
/** The owner walk over one live tree, built once for it. */
const ownerWalkOver = perObject((tree) => new OwnerWalk(new PlacedLiveTree(tree)));
/** The owner walk every question over `ctx` takes, so each walk shares each step. */
function ownerWalkOf(roots, ctx) {
    return ownerWalkOver(liveTreeOf(roots, ctx));
}
/**
 * The owner a `%Name` on the node at `path` registers with: the file the heading is
 * in, except an override heading inside an instance, which the instance keeps
 * (resource_format_text.cpp:264-265). An explicit `owner=` replaces that: `"."` is
 * the root of the file the heading is in, anything else the node at that root-relative path.
 */
export function claimOwnerOf(path, roots, ctx) {
    const walk = ownerWalkOf(roots, ctx);
    const { owner, node } = walk.to(path);
    if (node?.owner === undefined)
        return owner;
    const file = fileSeating(owner, path, ADDED);
    // `.` with a trail is the inherited root, whose base holds the owner the trail names.
    if (node.owner === '.' && node.ownerContentTrail === undefined)
        return file;
    const ownerPath = explicitOwnerPath(node.owner, node, file.path, walk);
    return ownerPath === undefined ? noOwner(walk.live) : walk.to(ownerPath).into;
}
/**
 * The live path of the owner an explicit `owner=` names, or the one the node's `owner_uid_path=`
 * finds where that walk finds no node (packed_scene.cpp:159-163). Undefined where neither finds one.
 */
function explicitOwnerPath(owner, { ownerContentTrail, ownerTrailFallback }, filePath, walk) {
    const walked = owner === NO_OWNER ? undefined : ownerLivePath(filePath, owner, walk);
    const walkFinds = walked !== undefined && (!ownerTrailFallback || !walkFindsNothing(walked, walk));
    if (walkFinds)
        return withContentTrailFollowed(walked, ownerContentTrail, walk);
    if (!ownerTrailFallback)
        return undefined;
    const recovered = ownerLivePath(filePath, ownerTrailFallback.owner, walk);
    return recovered && withContentTrailFollowed(recovered, ownerTrailFallback.ownerContentTrail, walk);
}
/**
 * Whether the loaded tree holds no node at `path`, so the `owner=` walk finds none. A scene still
 * loading below the nearest node above `path` may hold it, so that does not count.
 */
function walkFindsNothing(path, { live }) {
    for (let above = path; above !== null; above = parentPathOf(above)) {
        const link = live.tree.at(above);
        if (!link)
            continue;
        if (above === path)
            return false;
        const scenePath = link.step.instancePath;
        return !scenePath || live.tree.sceneNodes(scenePath) !== undefined;
    }
    return true;
}
/**
 * The live path of the owner an `owner_uid_path=` finds below the instance at `instancePath`, or
 * undefined where no heading of its scene answers the rest of the trail, or a scene it enters is
 * not loaded yet. Without a trail, `instancePath` itself.
 */
function withContentTrailFollowed(instancePath, trail, { live }) {
    if (trail === undefined)
        return instancePath;
    const scenePath = live.tree.at(instancePath)?.step.instancePath;
    const followed = scenePath ? live.tree.followTrail(scenePath, trail) : undefined;
    return followed?.status === 'found' ? pathUnder(instancePath, followed.path) : undefined;
}
const NO_NAMES = { paths: () => new Map() };
/** The claim owner of a node with no owner, whose table is empty. */
function noOwner(live) {
    return { path: '', roots: [], live, composed: NO_NAMES };
}
/**
 * The live path `owner` names from the file root at `rootPath`, or undefined where it climbs above
 * that root, which `get_node_or_null` answers with null (node.cpp:1919-1922). The seating replaced
 * each `%Name` its own tables answer, so one left here passes through an instance, whose own table
 * answers it, else its owner's (node.cpp:1930-1938).
 */
function ownerLivePath(rootPath, owner, walk) {
    let path = rootPath;
    for (const name of nodePathWalkNames(owner)) {
        if (name === '..' && path === rootPath)
            return undefined;
        const claimant = name.startsWith(UNIQUE_NODE_PREFIX)
            ? localSceneUniquePaths(walk.to(path).into).get(name)
            : undefined;
        path = claimant ?? simplifiedNodePath(joinPath(path, name));
    }
    return path;
}
/**
 * The local scene a local-to-scene resource on the node at `path` is set up against, and so the
 * node its NodePaths measure from: the node itself when it is an instance, else its owner
 * (packed_scene.cpp:707). A scene's own root is its own owner, so the outer root is itself. An
 * explicit `owner=` takes no part: a new node's properties land (:438) before its owner is set
 * (:566-568), so Godot reads no owner and falls back to the file root, and an override keeps
 * the sub-scene owner it already has.
 */
export function localSceneOf(path, roots, ctx) {
    return ownerWalkOf(roots, ctx).to(path).into;
}
/**
 * The `%Name` table a walk from the local scene root reads: its own table, else its owner's
 * (node.cpp:1930-1938). Only an instance root has an owner, so the outer root's is its own.
 */
export function localSceneUniquePaths(scene) {
    const own = ownerUniquePaths(scene);
    if (!scene.parent)
        return own;
    return new Map([...ownerUniquePaths(scene.parent), ...own]);
}
/** `paths` with each live path placed, the same object where no graft places any. */
function withPlacedLivePaths(paths, live) {
    if (![...paths.values()].some(isPlacedByGraft))
        return paths;
    return new Map([...paths].map(([key, path]) => [key, live.placed(path)]));
}
/**
 * `owner`'s `%Name` table at composed live paths: the one its collapse composed, else its own
 * file's claims. The outer root's own file's table is one shared object per tree.
 */
export function ownerUniquePaths(owner) {
    const composed = owner.composed ?? (owner.parent ? undefined : liveRootTable(owner));
    if (composed)
        return withPlacedLivePaths(rebased(composed.paths(), owner.path), owner.live);
    if (!owner.parent)
        return withPlacedLivePaths(cachedUniqueNameLivePaths(owner.roots), owner.live);
    return withPlacedLivePaths(rebased(cachedUniqueNamesBelowRoot(owner.roots), owner.path), owner.live);
}
/** `paths` from a node, spelt from the root through the node at `path`. */
function rebased(paths, path) {
    return new Map([...paths].map(([key, fromNode]) => [key, simplifiedLivePath(joinPath(path, fromNode))]));
}
/**
 * The table the live tree gave the outer root, or undefined where its file's own claims stand: an
 * inherited root's collapse composes one over its base, which owns the base's names
 * (packed_scene.cpp:227-231), and a root re-seated for a root fallback carries that seating's.
 */
function liveRootTable(outer) {
    const root = outer.roots[0];
    return root && outer.live.tree.at(root.name)?.step.node.uniqueNames;
}
//# sourceMappingURL=uniqueNameOwner.js.map